<template lang="pug">
div
  div.px-5.py-4
    template(v-if="isFetching")
      v-skeleton-loader(type="heading")
      v-skeleton-loader.mt-3(type="paragraph@2")
    template(v-else)
      div.d-flex.align-center
        div.text-xs.font-semibold.neutral700--text {{ treatment.name }}
        v-spacer
        div.clickable(@click="activeMedication = !activeMedication")
          y-chip.no-wrap(v-if="isMedicine" label="Mark as active" size="xs" outlined )
            template(#prepend)
              v-icon.mr-1(v-if="activeMedication" size="15" color="success500") $yzi-check-circle-solid
              v-icon.mr-1(v-else size="15" color="neutral500") mdi-radiobox-blank
        y-button.ml-2(label="Remove" outlined color="neutral400" background-color="var(--v-neutral100)" text-color="danger500" unset-min-width size="xxs" height="24" button-class="px-2" @click="showDeleteConfirmationDialog = true")
        y-button.ml-2(label="Save" unset-min-width size="xxs" height="24" button-class="px-2" :loading="isLoading" @click="saveData")
      //- TODO: replace with actual API fields when available
      div.d-flex.align-center.mt-1(v-if="treatment.product.sku || treatment.product.supplier" style="gap: 8px;")
        .yz-regular-body-2xs.text-neutral-500(v-if="treatment.product.sku") {{ treatment.product.sku }}
        yz-badge(v-if="treatment.product.supplier" color="neutral-200" text-color="neutral-700" border border-color="neutral-300") {{ treatment.product.supplier }}
      div.mt-2
        v-form(ref="form")
          v-row(dense)
            v-col(cols="2")
              div.text-xs.font-medium.neutral500--text Quantity
              v-text-field.text-xs.round-md.custom-height.mt-1(ref="quantity" v-model="quantity" outlined placeholder="0" type="number" :rules="inventoryEnabled ? rules.allowed_quantity : rules.has_quantity" hide-details dense)
            v-col(v-if="hasUomConversion" cols="2")
              div.text-xs.font-medium.neutral500--text UOM
              medication-unit-of-measure-dropdown.mt-1(v-model="selectedUom" :allowedValues="uomOptions" size="md" :fieldMinWidth="70" placeholder="UOM" :deletable="false")
            v-col(v-else-if="treatment.product.uom" cols="2")
              div.text-xs.font-medium.neutral500--text UOM
              v-text-field.text-xs.round-md.custom-height.mt-1(:value="treatment.product.uom" outlined dense hide-details readonly style="background-color: var(--v-neutral300)")
            v-col(v-if="!isDocument" :cols="hasUom ? 3 : 4")
              div.text-xs.font-medium.neutral500--text Price tier
              treatment-price-tier-dropdown.mt-1(v-model="priceTier" :item="treatmentItem" size="xs" @change="updateRate($event)")
            v-col(:cols="hasUom ? 2 : 3")
              div.text-xs.font-medium.neutral500--text Rate
              v-text-field.text-xs.round-md.custom-height.mt-1(v-model="rate" outlined placeholder="0" type="number" :rules="rules.must_positive_value" hide-details dense :readonly="!allowedPriceEdit" :style="!allowedPriceEdit ? 'background-color: var(--v-neutral300)' : ''" :prefix="$currency.symbol")
            v-col(:cols="hasUom ? 3 : 3")
              div.text-xs.font-medium.neutral500--text Amount
              v-text-field.text-xs.round-md.custom-height.mt-1(v-model="amount" outlined placeholder="0" type="number" hide-details dense readonly style="background-color: var(--v-neutral300)" :prefix="$currency.symbol")
          v-row(v-if="isMedicine" dense)
            v-col(cols="4")
              div.text-xs.font-medium.neutral500--text Indication
              treatment-indication-dropdown.mt-1(v-model="indication" size="xs")
            v-col(cols="4")
              div.text-xs.font-medium.neutral500--text Dosage
              v-row(dense)
                v-col(cols="4")
                  v-text-field.text-xs.round-md.custom-height.mt-1(v-model="dosageValue" outlined placeholder="0" type="number" hide-details dense)
                v-col(cols="8")
                  treatment-dosage-unit-dropdown.mt-1(v-model="dosageUnit" size="xs")
            v-col(cols="4")
              div.text-xs.font-medium.neutral500--text Frequency
              treatment-frequency-dropdown.mt-1(v-model="frequency" size="xs")
          v-row(v-if="isMedicine" dense)
            v-col(cols="4")
              div.text-xs.font-medium.neutral500--text Instruction
              treatment-instruction-dropdown.mt-1(v-model="instruction" size="xs")
            v-col(cols="4")
              div.text-xs.font-medium.neutral500--text Duration
              treatment-duration-dropdown.mt-1(v-model="duration" size="xs")
            v-col(cols="4")
              div.text-xs.font-medium.neutral500--text Precaution
              treatment-precaution-dropdown.mt-1(v-model="precaution" size="xs")
  v-divider
  .dialogs
    delete-confirmation-dialog(v-if="showDeleteConfirmationDialog" :open.sync="showDeleteConfirmationDialog" :loading="isDeleting" @cancel="showDeleteConfirmationDialog = false" @delete="confirmDeleteTreatment")
      delete-confirmation-details(:selectedTreatment="treatmentItem")
    y-dialog(v-if="showQuantityError" v-model="showQuantityError" persistent width="315" content-class="round-2xl")
      v-card.pa-6
        div.text-md.font-semibold.text-center Insufficient stock balance
        div.text-sm.text-center.mt-6 You do not have sufficient stock balance for this inventory.
        y-button.mt-6(label="Done" size="sm" block @click="showQuantityError = false")

</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import TreatmentPriceTierDropdown from '@/components/clinic/consultation/TreatmentPriceTierDropdown.vue'
import TreatmentDosageUnitDropdown from '@/components/clinic/consultation/TreatmentDosageUnitDropdown.vue'
import TreatmentFrequencyDropdown from '@/components/clinic/consultation/TreatmentFrequencyDropdown.vue'
import TreatmentIndicationDropdown from '@/components/clinic/consultation/TreatmentIndicationDropdown.vue'
import TreatmentInstructionDropdown from '@/components/clinic/consultation/TreatmentInstructionDropdown.vue'
import TreatmentDurationDropdown from '@/components/clinic/consultation/TreatmentDurationDropdown.vue'
import TreatmentPrecautionDropdown from '@/components/clinic/consultation/TreatmentPrecautionDropdown.vue'
import DeleteConfirmationDetails from '@/components/clinic/consultation/treatments/treatmentTableItems/DeleteConfirmationDetails.vue'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'
import MedicationUnitOfMeasureDropdown from '@/components/clinic/inventory/MedicationUnitOfMeasureDropdown.vue'

export default {
  components: {
    TreatmentPriceTierDropdown, TreatmentDosageUnitDropdown, TreatmentFrequencyDropdown, TreatmentIndicationDropdown,
    TreatmentInstructionDropdown, TreatmentDurationDropdown, TreatmentPrecautionDropdown,
    DeleteConfirmationDetails, DeleteConfirmationDialog, MedicationUnitOfMeasureDropdown
  },
  props: ['treatment'],
  data () {
    return {
      isFetching: false,
      isLoading: false,
      isDeleting: false,
      showDeleteConfirmationDialog: false,
      showQuantityError: false,
      activeMedication: false,
      priceTier: null,
      dosageUnit: null,
      dosageValue: null,
      frequency: null,
      indication: null,
      instruction: null,
      duration: null,
      precaution: null,
      treatmentItem: null,
      selectedUom: null,
      quantity: 1,
      rate: 1,
      rules: {
        has_quantity: [v => !!v || 'This field is required', v => v > 0 || 'This field is required'],
        must_positive_value: [v => (v != null && v.toString().trim().length) || 'This field is required', v => v >= 0 || 'This field must have positive value'],
        allowed_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required', v => v <= this.inventoryQty || 'Item quantity more than available stock'],
      },
    }
  },
  computed: {
    ...mapState('user', ['member']),
    ...mapGetters({
      company: 'getCompany',
      consultation: 'treatment/getConsultation',
      registration: 'treatment/getRegistration',
      patient: 'treatment/getPatient',
      leadPid: 'treatment/getLeadPid',
      activeMedications: 'treatment/getActiveMedications',
      isDoctor: 'user/isDoctor',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq',
    }),
    amount () {
      return Number(this.quantity || 0) * Number(this.rate || 0)
    },
    inventoryEnabled () {
      return this.treatment.product.inventory_enabled
    },
    inventoryQty () {
      return this.treatment.product.inventory_qty + this.treatment.quantity
    },
    allowedPriceEdit () {
      // admin can edit price
      if (this.isAdmin) return true
      // 'lock_treatment_plan' is for CA
      if (this.company.genes.find(gene => gene.feature === 'lock_treatment_plan') && !this.isAdminOrDoctor) return false
      // 'lock_treatment_plan_price_edit' is for doctor
      if (this.company.genes.find(gene => gene.feature === 'lock_treatment_plan_price_edit') && this.isDoctor) return false
      return true
    },
    isAdmin () {
      return this.member.roles.includes('Admin')
    },
    isAdminOrDoctor () {
      if (this.isAdmin || this.isDoctor) return true
      return false
    },
    isMedicine () {
      if (this.treatment.product.medication && this.treatment.product.medication.id) {
        return true
      }
      return false
    },
    isDocument () {
      if (this.treatment.product.type === 'documents') {
        return true
      }
      return false
    },
    hasUom () {
      return this.hasUomConversion || !!this.treatment.product.uom
    },
    hasUomConversion () {
      return this.treatment.product.uom_conversion_enabled && this.treatment.product.uom_conversions && this.treatment.product.uom_conversions.length > 0
    },
    uomOptions () {
      const units = []
      if (this.treatment.product.uom_conversions) {
        this.treatment.product.uom_conversions.forEach(c => {
          if (c.uom && !units.includes(c.uom)) units.push(c.uom)
          if (c.convert_to && !units.includes(c.convert_to)) units.push(c.convert_to)
        })
      }
      return units
    },
    isActiveMedication () {
      if (this.treatment.product.medication && this.treatment.product.medication.id) {
        if (this.activeMedications.find(f => f.m_id === this.treatment.product.medication.id)) {
          return true
        }
      }
      return false
    }
  },
  watch: {
    selectedUom () {
      if (!this.hasUomConversion || this.isDocument || !this.priceTier) return
      if (!('price_per_uom' in this.priceTier)) {
        this.refetchPriceTierForUom()
      } else if (Array.isArray(this.priceTier.price_per_uom) && this.priceTier.price_per_uom.length) {
        this.updateRate(this.priceTier)
      }
    }
  },
  mounted () {
    this.setData()
  },
  methods: {
    ...mapActions(['showMessage']),
    setData () {
      this.quantity = this.treatment.quantity
      this.rate = (Number(this.treatment.price) / this.$currency.multiplier)
      this.selectedUom = this.treatment.product.uom_conversion_enabled
        ? (this.treatment.uom || this.treatment.product.uom || (this.treatment.product.uom_conversions?.length ? this.treatment.product.uom_conversions[this.treatment.product.uom_conversions.length - 1].convert_to : null))
        : (this.treatment.uom || this.treatment.product.uom || null)
      this.dosageUnit = this.treatment?.extra_data?.dosage_unit
      this.dosageValue = this.treatment?.extra_data?.dosage_value
      this.frequency = this.treatment?.extra_data?.frequency
      this.indication = this.treatment?.extra_data?.indication
      this.instruction = this.treatment?.extra_data?.instruction
      this.duration = this.treatment?.extra_data?.duration
      this.precaution = this.treatment?.extra_data?.precaution
      if (!this.isDocument) {
        this.priceTier = {
          id: this.treatment?.extra_data?.price_tier || null,
          name: this.treatment?.extra_data?.price_tier_name || null,
          price: this.treatment.product.price || null,
        }
      }
      if (this.isMedicine) {
        this.$nextTick(() => {
          this.activeMedication = this.isActiveMedication
        })
      }
      this.treatmentItem = {
        id: this.treatment.product.id,
        name: this.treatment.product.name,
      }
    },
    updateRate (pricetier) {
      if (!pricetier || !Object.keys(pricetier).length) return
      let price = Number(pricetier.price || 0)
      if (this.hasUomConversion && Array.isArray(pricetier.price_per_uom) && pricetier.price_per_uom.length && this.selectedUom) {
        const match = pricetier.price_per_uom.find(p => p.uom === this.selectedUom)
        if (match) price = Number(match.price || 0)
      }
      this.rate = price / this.$currency.multiplier
    },
    refetchPriceTierForUom () {
      this.isFetching = true
      const params = {
        company: this.isHq ? this.hqSelectedCompany?.id : this.company.id,
        product: this.treatment.product.id
      }
      if (this.registration?.default_payment && this.registration.default_payment.type !== 'self-pay') {
        params.payment_methods = this.registration.default_payment.panel.id
      } else {
        params.payment_channel = 'self-pay'
      }
      const currentTierId = this.priceTier && this.priceTier.id
      this.$axios.get('/product_pricetiers', { params })
        .then(resp => {
          if (!resp.data.count) return
          const match = currentTierId ? resp.data.results.find(t => t.id === currentTierId) : resp.data.results[0]
          const tier = match || resp.data.results[0]
          this.priceTier = tier
          this.updateRate(tier)
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    saveData () {
      if (!this.$refs.form.validate()) {
        if (this.$refs.quantity && this.$refs.quantity.hasError) {
          if (this.$refs.quantity.messagesToDisplay[0] == 'Item quantity more than available stock') {
            this.showQuantityError = true
            return
          }
        }
        return this.showMessage(['error', this.$t('error_message.please_fill_out_all_required')])
      } if (!this.isDocument && !this.priceTier) {
        return this.showMessage(['error', this.$t('error_message.please_fill_out_all_required')])
      } if (this.dosageUnit || this.dosageValue) {
        if (!this.dosageUnit || !this.dosageValue) {
          return this.showMessage(['error', 'Please enter the correct dosage amount and unit'])
        }
      }
      this.isLoading = true
      let payload = {
        product: this.treatment.product.id,
        quantity: this.quantity,
        price: Math.round(Number(this.rate) * this.$currency.multiplier),
        ...(this.treatment.product.uom_conversion_enabled && this.selectedUom ? { uom: this.selectedUom } : {})
      }
      if (this.priceTier) {
        payload.extra_data = {
          price_tier: this.priceTier.id,
          price_tier_name: this.priceTier.name,
        }
      }
      if (this.isMedicine) {
        payload.extra_data = {
          ...payload.extra_data,
          dosage_unit: this.dosageUnit,
          dosage_value: this.dosageValue,
          frequency: this.frequency,
          duration: this.duration,
          instruction: this.instruction,
          precaution: this.precaution,
          indication: this.indication,
        }
      }
      const endpoint = `/clinics/${this.company.slug}/invoices/${this.leadPid}`
      this.$axios.patch(endpoint, { products: [{ id: this.treatment.id, ...payload }] })
        .then(resp => {
          this.updateActivityLog(endpoint, payload, resp.data, 'PATCH', resp.data.id, 'consultation', `Update treatment plan (${this.leadPid})`)
          this.updateActiveMedication()
        })
        .catch((err) => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response?.status === 400 && err.response?.data?.code === '444101') {
            message = 'Item quantity more than available stock. Please adjust the quantity'
          }       
          if (err.response.data) {
            if (err.response.data.detail) {
              message = err.response.data.detail
            } else {
              let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
              if (data) { message = data }
            }
          }
          this.showMessage(['error', message])
          this.isLoading = false
        })
    },
    updateActiveMedication () {
      if (!this.isMedicine) {
        this.finalizeProcess()
        return
      }
      if (this.isActiveMedication !== this.activeMedication) {
        if (this.isActiveMedication) {
          let actmed = this.activeMedications.find(f => f.m_id === this.treatment.product.medication.id)
          const endpoint = `clinics/${this.company.slug}/active_medications/${actmed.id}`
          this.$axios.delete(endpoint)
            .catch(() => {})
            .finally(() => {
              this.updateActivityLog(endpoint, null, null, 'DELETE', this.treatment.product.medication.id, 'active_medication', 'deactivate medication')
              this.finalizeProcess()
            })
        } else {
          const endpoint = `clinics/${this.company.slug}/active_medications`
          let payload = {
            medication: this.treatment.product.medication.id,
            patient: this.patient.id
          }
          this.$axios.post(endpoint, payload)
            .then(resp => {
              this.updateActivityLog(endpoint, payload, resp.data, 'POST', resp.data.id, 'active_medication', 'add active medication')
              this.finalizeProcess()
            })
        }
      } else {
        this.finalizeProcess()
      }
    },
    confirmDeleteTreatment() {
      this.isDeleting = true
      const id = this.treatment.id
      const endpoint = `/clinics/${this.company.slug}/invoices/${this.leadPid}`
      this.$axios.patch(endpoint, { products: [{ id, product: this.treatment.product.id, quantity: 0, ...(this.treatment.product.uom_conversion_enabled && (this.treatment.uom || this.treatment.product.uom) ? { uom: this.treatment.uom || this.treatment.product.uom } : {}) }] })
        .then(() => {
          this.updateActivityLog(endpoint, null, null, 'DELETE', id, 'consultation', `Delete treatment plan (${this.leadPid})`)
          this.$emit('updated')
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isDeleting = false
          this.showDeleteConfirmationDialog = false
        })
    },
    updateActivityLog (endpoint, payload, response, method, id, objectRef, activity) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: objectRef,
        activity: activity,
        raw_data: { endpoint, payload, response, method }
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
    finalizeProcess () {
      this.showMessage(['success', 'Treatment plan successfully updated'])
      this.$emit('updated')
      this.isLoading = false
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-text-field--outlined.error--text fieldset {
    border-color: var(--v-danger500) !important;
  }
  .custom-height {
    height: 32px;
    fieldset {
      height: 37px;
    }
    .v-text-field__slot {
      height: 32px;
    }
  }
}
</style>